import { secureApiService } from './apiService';
import { getPortal } from '../utils/portal';
import { isValidEmail } from '../utils/emailValidation'; // M2-L1 — source unique (plus de regex inline)

// 🔒 VALIDATION DES CREDENTIALS ADMIN - VERSION CORRIGÉE
const validateAdminCredentials = (credentials) => {
  if (!credentials || typeof credentials !== 'object') {
    throw new Error('Données de connexion invalides');
  }
  
  // Validation plus flexible pour le développement
  if (!credentials.email || !credentials.mot_de_passe) {
    throw new Error('Email et mot de passe requis');
  }
  
  // Code admin facultatif au niveau service : il n'est requis QUE pour les comptes
  // à privilège élevé (super admin / admin compagnie), ce que le backend vérifie.
  // Les agents / chef d'agence se connectent sans code partagé (portail staff).

  if (!isValidEmail(credentials.email)) {
    throw new Error('Format d\'email invalide');
  }
  
  return { ...credentials };
};

// 🔒 VALIDATION ID
const validateId = (id, entityName = '') => {
  if (id === null || id === undefined || id === '') {
    throw new Error(`ID ${entityName} invalide`);
  }
  return String(id);
};

export const adminService = {
  // 🔐 AUTHENTIFICATION ADMIN SÉCURISÉE
  login: (credentials) => {
    const sanitizedCredentials = validateAdminCredentials(credentials);
    // 🔒 Isolation par domaine : on transmet le portail (hostname) pour que le backend
    // refuse aussi un rôle hors-portail (défense en profondeur).
    return secureApiService.post("/admin/auth/login", { ...sanitizedCredentials, portal: getPortal() });
  },

  logout: () => secureApiService.post("/admin/auth/logout"),

  signup: (adminData) => {
    const sanitized = validateAdminCredentials(adminData);
    return secureApiService.post("/admin/auth/signup", sanitized);
  },

  // 📊 DASHBOARD ADMIN
  getDashboardStats: () => 
    secureApiService.get('/admin/dashboard/stats', {}, { 
      useCache: true, 
      cacheTimeout: 60000 
    }),

  // 👤 PROFIL ADMIN
  getAdminProfile: () =>
    secureApiService.get('/admin/users/profile', {}, {
      useCache: false
    }),

  updateAdminProfile: (data) =>
    secureApiService.put('/admin/users/profile', data),

  // 👥 GESTION UTILISATEURS SÉCURISÉE
  getUsers: (filters = {}) => 
    secureApiService.get('/admin/users', { params: filters }, { 
      useCache: true, 
      cacheTimeout: 30000 
    }),

  searchUsers: (query, filters = {}) => {
    const sanitizedQuery = query.replace(/[<>]/g, '').substring(0, 100);
    return secureApiService.get('/admin/users/search', { 
      params: { q: sanitizedQuery, ...filters } 
    }, { 
      useCache: false 
    });
  },

  getUserById: (id) => {
    const validId = validateId(id, 'utilisateur');
    return secureApiService.get(`/admin/users/${validId}`, {}, { 
      useCache: false 
    });
  },

  updateUserStatus: (id, statut) => {
    const validId = validateId(id, 'utilisateur');
    const allowedStatus = ['actif', 'inactif', 'suspendu'];
    
    if (!allowedStatus.includes(statut)) {
      throw new Error('Statut utilisateur invalide');
    }
    
    return secureApiService.put(`/admin/users/${validId}/statut`, { statut });
  },

  deleteUser: (id) => {
    const validId = validateId(id, 'utilisateur');
    return secureApiService.delete(`/admin/users/${validId}`);
  },

  // ⚡ Action de masse utilisateurs (changer statut) — 1 requête
  bulkUsers: (ids, statut) =>
    secureApiService.post('/admin/users/bulk', { ids, statut }),

  // 🗑️ Suppression en masse (M1-L3) — bilan ligne par ligne (deleted|soft_deleted|refused)
  bulkDeleteUsers: (ids) =>
    secureApiService.post('/admin/users/bulk-delete', { ids }),

  // 🪪 S2 — « Mes sessions » : sessions du COMPTE CONNECTÉ (jamais d'autrui). Liste + fermeture.
  getMySessions: () =>
    secureApiService.get('/admin/me/sessions', {}, { useCache: false }),
  closeMySession: (sessionId) =>
    secureApiService.delete(`/admin/me/sessions/${encodeURIComponent(sessionId)}`),
  closeOtherSessions: () =>
    secureApiService.delete('/admin/me/sessions'),

  // 🔐 Sessions/appareils/IP d'un utilisateur + forcer la déconnexion
  getUserSessions: (id) =>
    secureApiService.get(`/admin/users/${validateId(id, 'utilisateur')}/sessions`, {}, { useCache: false }),
  forceLogoutUser: (id) =>
    secureApiService.post(`/admin/users/${validateId(id, 'utilisateur')}/force-logout`, {}),
  // 🔑 Réinitialiser le mot de passe — renvoie { credentials: { email, password } }
  resetUserPassword: (id) =>
    secureApiService.post(`/admin/users/${validateId(id, 'utilisateur')}/reset-password`, {}),

  // 🔓 Débloquer un compte verrouillé par l'anti-brute-force (sans changer le mot de passe)
  unlockUser: (id) =>
    secureApiService.post(`/admin/users/${validateId(id, 'utilisateur')}/unlock`, {}),

  // 🛡️ Promouvoir / changer le rôle d'un utilisateur (super-admin)
  // role ∈ SUPER_ADMIN | ADMIN_COMPANY | OPERATOR | DRIVER | CLIENT ; action 'grant'|'revoke'
  promoteUser: (user_id, role, company_id = null, action = 'grant') =>
    secureApiService.post('/admin/super/promote-user', { user_id, role, company_id, action }),

  // 🚌 GESTION TRAJETS SÉCURISÉE
  getTrajets: (filters = {}) => 
    secureApiService.get('/admin/trajets', { params: filters }, { 
      useCache: true, 
      cacheTimeout: 30000 
    }),

  createTrajet: (data) =>
    secureApiService.post('/admin/trajets', data),

  // 🧭 LIGNES (gabarits d'itinéraire réutilisables) — scopées compagnie côté backend
  // 🔎 Point E — Journal d'audit (super-admin) : qui a fait quoi, filtrable par compagnie/action/dates.
  getAuditLogs: (filters = {}) =>
    secureApiService.get('/admin/super/audit-logs/rbac', { params: filters }, { useCache: false }),

  getLignes:          (filters = {}) => secureApiService.get('/admin/lignes', { params: filters }, { useCache: false }),
  getLigneById:       (id) => secureApiService.get(`/admin/lignes/${id}`, {}, { useCache: false }),
  getLigneVilles:     () => secureApiService.get('/admin/lignes/villes-suggestions', {}, { useCache: false }),
  createLigne:        (data) => secureApiService.post('/admin/lignes', data),
  updateLigne:        (id, data) => secureApiService.put(`/admin/lignes/${id}`, data),
  deleteLigne:        (id) => secureApiService.delete(`/admin/lignes/${id}`),

  // 📅 Création de série de trajets (plage de dates + répétition)
  createTrajetSeries: (data) =>
    secureApiService.post('/admin/trajets/series', data),

  // ⚡ Action de masse trajets (archive/cancel/price/bus) — 1 requête
  bulkTrajets: (data) =>
    secureApiService.post('/admin/trajets/bulk', data),

  // ⚡ Action de masse bus (archive) — 1 requête
  bulkBus: (data) =>
    secureApiService.post('/admin/bus/bulk', data),

  // ⚡ Action de masse réservations (changer statut) — 1 requête
  bulkReservations: (data) =>
    secureApiService.post('/admin/reservations/bulk', data),

  bulkDeleteReservations: (ids) =>
    secureApiService.post('/admin/reservations/bulk-delete', { ids }),

  updateTrajet: (id, data) => {
    const validId = validateId(id, 'trajet');
    return secureApiService.put(`/admin/trajets/${validId}`, data);
  },

  deleteTrajet: (id, action = null) => {
    const validId = validateId(id, 'trajet');
    return secureApiService.delete(`/admin/trajets/${validId}${action ? `?action=${action}` : ''}`);
  },

  // 🎫 GESTION RÉSERVATIONS SÉCURISÉE
  getReservations: (filters = {}) => 
    secureApiService.get('/admin/reservations', { params: filters }, { 
      useCache: true, 
      cacheTimeout: 15000 
    }),

  updateReservationStatus: (id, status) => {
    const validId = validateId(id, 'réservation');
    const allowedStatus = ['confirmee', 'en_attente', 'annulee', 'termine'];

    if (!allowedStatus.includes(status)) {
      throw new Error('Statut réservation invalide');
    }

    return secureApiService.put(`/admin/reservations/${validId}`, { etat_reservation: status });
  },

  // ✏️ MODIFIER UNE RÉSERVATION (statut, siège, montant) — admin
  updateReservation: (id, data) => {
    const validId = validateId(id, 'réservation');
    return secureApiService.put(`/admin/reservations/${validId}`, data);
  },

  // 🗑️ SUPPRIMER UNE RÉSERVATION (soft-delete → corbeille)
  deleteReservation: (id) => {
    const validId = validateId(id, 'réservation');
    return secureApiService.delete(`/admin/reservations/${validId}`);
  },

  // 🎫 CRÉER RÉSERVATION POUR UN CLIENT (admin)
  createReservation: (data) =>
    secureApiService.post('/admin/reservations/create', data),

  // 🎯 Cockpit métier par rôle (KPIs scopés compagnie + agence)
  getCockpit: () =>
    secureApiService.get('/admin/dashboard/cockpit', {}, { useCache: false }),

  // 💸 FRAIS DE SERVICE (super-admin) — grille par compagnie / plateforme
  getServiceFees: (params = {}) =>
    secureApiService.get('/admin/service-fees', { params }, { useCache: false }),
  createServiceFee: (data) =>
    secureApiService.post('/admin/service-fees', data),
  updateServiceFee: (id, data) =>
    secureApiService.put(`/admin/service-fees/${validateId(id, 'frais')}`, data),
  deleteServiceFee: (id) =>
    secureApiService.delete(`/admin/service-fees/${validateId(id, 'frais')}`),

  // 🔍 RECHERCHE UNIVERSELLE (bus, trajets, réservations, chauffeurs, sociétés)
  globalSearch: (q) =>
    secureApiService.get('/admin/search', { params: { q } }, { useCache: false }),

  // 🖨 BILLET PDF (blob) — pour impression / réimpression immédiate
  getTicketPdfBlob: (reservationId) =>
    secureApiService.get(`/admin/documents/billet/${validateId(reservationId, 'réservation')}`,
      { responseType: 'blob' }, { useCache: false }),

  // ✉️ ENVOYER LE BILLET PAR EMAIL (après-vente)
  emailTicket: (reservationId) =>
    secureApiService.post(`/admin/documents/billet/${validateId(reservationId, 'réservation')}/email`, {}),

  // 🚍 GESTION BUS
  // §12 — le backend pagine (défaut 20). Pour un SÉLECTEUR (ex. formulaire trajet) il faut TOUS
  // les bus assignables : on demande la limite max (100). Filtres optionnels surchargeables.
  getBus: (filters = {}) =>
    secureApiService.get('/admin/bus', { params: { limit: 100, ...filters } }, {
      useCache: true,
      cacheTimeout: 60000
    }),

  createBus: (data) =>
    secureApiService.post('/admin/bus', data),

  updateBus: (id, data) => {
    const validId = validateId(id, 'bus');
    return secureApiService.put(`/admin/bus/${validId}`, data);
  },

  deleteBus: (id, action = null) => {
    const validId = validateId(id, 'bus');
    return secureApiService.delete(`/admin/bus/${validId}${action ? `?action=${action}` : ''}`);
  },

  updateChauffeur: (id, data) => {
    const validId = validateId(id, 'chauffeur');
    return secureApiService.put(`/admin/chauffeurs/${validId}`, data);
  },

  deleteChauffeur: (id) => {
    const validId = validateId(id, 'chauffeur');
    return secureApiService.delete(`/admin/chauffeurs/${validId}`);
  },

  // ⚡ Action de masse chauffeurs (archive) — 1 requête
  bulkChauffeurs: (ids, action = 'archive') =>
    secureApiService.post('/admin/chauffeurs/bulk', { ids, action }),

  // ⚡ Action de masse avis (modérer / supprimer)
  bulkAvis: (ids, action, statut) =>
    secureApiService.post('/admin/avis/bulk', { ids, action, statut }),

  // ⚡ Action de masse sociétés (archiver)
  bulkSocietes: (ids, action = 'archive') =>
    secureApiService.post('/admin/societes/bulk', { ids, action }),

  // ⚡ Action de masse agences (changer statut : actif/inactif/suspendu)
  bulkAgences: (ids, statut) =>
    secureApiService.post('/admin/agences/bulk', { ids, action: 'statut', statut }),

  // ⭐ GESTION AVIS
  getAvis: (filters = {}) =>
    secureApiService.get('/admin/avis', { params: filters }, { useCache: false }),

  updateAvisStatut: (id, statut) => {
    const validId = validateId(id, 'avis');
    const allowed = ['approuve', 'rejete', 'en_attente'];
    if (!allowed.includes(statut)) throw new Error('Statut avis invalide');
    return secureApiService.put(`/admin/avis/${validId}/statut`, { statut });
  },

  deleteAvis: (id) => {
    const validId = validateId(id, 'avis');
    return secureApiService.delete(`/admin/avis/${validId}`);
  },

  // 📧 GESTION CONTACTS SÉCURISÉE
  getContacts: () => 
    secureApiService.get('/admin/contacts', {}, { 
      useCache: true, 
      cacheTimeout: 30000 
    }),

  updateContactStatus: (id, statut) => {
    const validId = validateId(id, 'contact');
    const allowedStatus = ['nouveau', 'traite', 'archive'];

    if (!allowedStatus.includes(statut)) {
      throw new Error('Statut contact invalide');
    }

    return secureApiService.put(`/admin/contacts/${validId}/statut`, { statut });
  },

  // ⚙️ PARAMÈTRES SYSTÈME
  getSystemSettings: () => 
    secureApiService.get('/admin/system/settings', {}, { 
      useCache: false 
    }),

  updateSystemSettings: (data) => 
    secureApiService.put('/admin/system/settings', data),

  getSystemLogs: (filters = {}) => 
    secureApiService.get('/admin/system/logs', { params: filters }, { 
      useCache: false 
    }),

  triggerMaintenance: (data) => 
    secureApiService.post('/admin/system/maintenance', data),

  // Sauvegarde RÉELLE : réponse binaire (.sql.gz) → responseType blob pour déclencher un téléchargement.
  createBackup: () =>
    secureApiService.post('/admin/system/backup', {}, { responseType: 'blob' }),

  // Aperçu de sauvegarde (vérif sans télécharger) : tables + nb de lignes + total.
  getBackupPreview: () =>
    secureApiService.get('/admin/system/backup/preview', {}, { useCache: false }),

  // ⚠️ Incidents (remontée centralisée des erreurs) — Super Admin.
  // 🔧 C4 — les query params doivent être passés dans { params } (2e arg = config axios),
  // sinon axios ne les envoie PAS → filtres/pagination silencieusement ignorés.
  getIncidents: (params = {}) =>
    secureApiService.get('/admin/system/incidents', { params }, { useCache: false }),
  getIncidentDetail: (id) =>
    secureApiService.get(`/admin/system/incidents/${id}`, {}, { useCache: false }),
  getIncidentsDigest: (hours = 24) =>
    secureApiService.get('/admin/system/incidents/digest', { params: { hours } }, { useCache: false }),

  getSystemHealth: () =>
    secureApiService.get('/admin/system/health', {}, {
      useCache: false
    }),

  cleanupLogs: (daysOld = 30) =>
    secureApiService.delete('/admin/system/logs/cleanup', { data: { days_old: daysOld } }),

  // 🎟️ CLASSES DE SIÈGES & TARIFS (par compagnie)
  getSeatClasses:    () => secureApiService.get('/admin/seat-classes/classes', {}, { useCache: false }),
  createSeatClass:   (data) => secureApiService.post('/admin/seat-classes/classes', data),
  updateSeatClass:   (id, data) => secureApiService.put(`/admin/seat-classes/classes/${id}`, data),
  deleteSeatClass:   (id) => secureApiService.delete(`/admin/seat-classes/classes/${id}`),
  setBusSeatPlan:    (busId, data) => secureApiService.put(`/admin/seat-classes/bus/${busId}/plan`, data),
  getTrajetSeatPrices: (trajetId) => secureApiService.get(`/admin/seat-classes/trajets/${trajetId}/prices`, {}, { useCache: false }),
  setTrajetSeatPrices: (trajetId, prices) => secureApiService.put(`/admin/seat-classes/trajets/${trajetId}/prices`, { prices }),

  // 🏢 GESTION SOCIÉTÉS
  getSocietes: () =>
    secureApiService.get('/admin/societes', {}, {
      useCache: true,
      cacheTimeout: 60000
    }),

  createSociete: (data) =>
    secureApiService.post('/admin/societes', data),

  updateSociete: (id, data) => {
    const validId = validateId(id, 'societe');
    return secureApiService.put(`/admin/societes/${validId}`, data);
  },

  deleteSociete: (id, action = null) => {
    const validId = validateId(id, 'societe');
    return secureApiService.delete(`/admin/societes/${validId}${action ? `?action=${action}` : ''}`);
  },

  // 🚗 GESTION CHAUFFEURS
  // filters : { q, page, limit }. La recherche est désormais serveur (nom/prénom/téléphone/
  // permis/identifiant), donc on NE filtre plus en mémoire sur une page tronquée. Le `q` est
  // inclus dans la clé de cache (buildCacheKey) → pas de collision entre deux recherches.
  getChauffeurs: (filters = {}) =>
    secureApiService.get('/admin/chauffeurs', { params: { limit: 100, ...filters } }, {
      useCache: true,
      cacheTimeout: 60000
    }),

  createChauffeur: (data) =>
    secureApiService.post('/admin/chauffeurs', data),

  createDriverAccount: (data) =>
    secureApiService.post('/admin/super/create-driver', data),

  updateChauffeurFull: (id, data) => {
    const validId = validateId(id, 'chauffeur');
    return secureApiService.put(`/admin/chauffeurs/${validId}`, data);
  },

  // 📊 ANALYTICS VISITEURS (super admin)
  getVisitorStats: () =>
    secureApiService.get('/admin/analytics/visitors', {}, { useCache: false }),
  // ⚠️ getAnalyticsSummary / getRevenueByDay / getOccupancy RETIRÉS (2026-09-27) : endpoints
  // /admin/analytics/* morts (aucun écran ne les appelait). Source analytique = /admin/reports/*.

  // 💰 RAPPORTS FINANCIERS
  getFinancialReports: (filters = {}) =>
    secureApiService.get('/admin/reports/financial', { params: filters }, { useCache: false }),

  getReportTrajets: (filters = {}) =>
    secureApiService.get('/admin/reports/trajets', { params: filters }, { useCache: false }),

  // 📊 Analyses Premium (affluence, annulation, remboursements, satisfaction)
  getPremiumAnalytics: (filters = {}) =>
    secureApiService.get('/admin/reports/premium-analytics', { params: filters }, { useCache: false }),

  // 💎 Analyses Wele+ (marge plateforme/trajet, suivi brut/commission/net, multi-agences)
  getWelePlusAnalytics: (filters = {}) =>
    secureApiService.get('/admin/reports/weleplus-analytics', { params: filters }, { useCache: false }),

  getReportPaiements: (filters = {}) =>
    secureApiService.get('/admin/reports/paiements', { params: filters }, { useCache: false }),

  // 📤 EXPORT MULTI-FORMAT (blob responses)
  exportReportCsv: (params = {}) =>
    secureApiService.get('/admin/reports/export/csv', { params, responseType: 'blob' }, { useCache: false }),

  exportReportExcel: (params = {}) =>
    secureApiService.get('/admin/reports/export/excel', { params, responseType: 'blob' }, { useCache: false }),

  exportReportPdf: (params = {}) =>
    secureApiService.get('/admin/reports/export/pdf', { params, responseType: 'blob' }, { useCache: false }),

  getExportHistory: () =>
    secureApiService.get('/admin/reports/export/history', {}, { useCache: false }),

  // 📤 EXPORT GÉNÉRIQUE — tout écran de rapport (présent/futur), PDF/Excel/CSV.
  // `doc` = { title, subtitle, filename, date_debut, date_fin, kpis[], tables[], charts[] }.
  // charts[] = images PNG capturées à l'écran (dataURL) → le serveur les insère dans le PDF.
  exportReportGeneric: (format, doc = {}) =>
    secureApiService.post(`/admin/reports/export/generic?format=${encodeURIComponent(format)}`, doc,
      { responseType: 'blob' }),

  // 📢 NOTIFICATIONS DE MASSE (email / SMS)
  // Transforme le payload frontend {target, channel, subject, message} → backend {titre_fr, message_fr, send_email, send_sms}
  sendMassNotification: (data) => {
    const payload = {
      titre_fr: data.subject || data.message?.substring(0, 80) || 'Notification',
      message_fr: data.message,
      send_email: data.channel === 'email',
      send_sms: data.channel === 'sms',
      // 🎯 Ciblage réel (avant : ignoré → tout partait à tous les actifs).
      target: data.target || 'all',
      specific_email: data.specific_email || undefined,
    };
    return secureApiService.post('/admin/notifications/mass', payload);
  },

  // 💸 REMBOURSEMENTS (liste compagnie + confirmation manuelle Mobile Money)
  getRefunds: () => secureApiService.get('/admin/paiements/refunds', {}, { useCache: false }),
  confirmManualRefund: (id) => secureApiService.post(`/admin/paiements/refunds/${id}/confirm`),

  // 💵 PAIEMENT CASH À LA GARE
  createCashPayment: (data) =>
    secureApiService.post('/admin/paiements/cash', data),

  // 🚏 GESTION ARRÊTS
  getArrets: (trajet_id = null) =>
    secureApiService.get('/admin/arrets', { params: trajet_id ? { trajet_id } : {} }, { useCache: false }),

  getArretsByTrajet: (trajet_id) =>
    secureApiService.get(`/admin/arrets/trajet/${trajet_id}`, {}, { useCache: false }),

  createArret: (data) =>
    secureApiService.post('/admin/arrets', data),

  updateArret: (id, data) =>
    secureApiService.put(`/admin/arrets/${validateId(id, 'arrêt')}`, data),

  deleteArret: (id) =>
    secureApiService.delete(`/admin/arrets/${validateId(id, 'arrêt')}`),

  // 📍 GPS TRACKING
  getGpsLive: (params = {}) =>
    secureApiService.get('/admin/gps/live', { params }, { useCache: false }),

  getGpsHistorique: (chauffeurId, params = {}) =>
    secureApiService.get(`/admin/gps/historique/${validateId(chauffeurId, 'chauffeur')}`, { params }, { useCache: false }),

  getGpsAlertes: (params = {}) =>
    secureApiService.get('/admin/gps/alertes', { params }, { useCache: false }),

  traiterGpsAlerte: (id, data) =>
    secureApiService.patch(`/admin/gps/alertes/${validateId(id, 'alerte')}`, data),

  // 📬 MESSAGING INBOX
  getConversations: (params = {}) =>
    secureApiService.get('/admin/messages', { params }, { useCache: false }),

  getConversationThread: (id) =>
    secureApiService.get(`/admin/messages/${validateId(id, 'conversation')}`, {}, { useCache: false }),

  replyToConversation: (id, data) =>
    secureApiService.post(`/admin/messages/${validateId(id, 'conversation')}/reply`, data),

  updateConversationStatus: (id, statut) =>
    secureApiService.put(`/admin/messages/${validateId(id, 'conversation')}/status`, { statut }),

  moveConversationToFolder: (id, folder) =>
    secureApiService.put(`/admin/messages/${validateId(id, 'conversation')}/folder`, { folder }),

  setConversationPriority: (id, priority) =>
    secureApiService.put(`/admin/messages/${validateId(id, 'conversation')}/priority`, { priority }),

  assignConversation: (id, agent_id) =>
    secureApiService.put(`/admin/messages/${validateId(id, 'conversation')}/assign`, { agent_id }),

  markConversationRead: (id) =>
    secureApiService.put(`/admin/messages/${validateId(id, 'conversation')}/read`, {}),

  markConversationUnread: (id) =>
    secureApiService.put(`/admin/messages/${validateId(id, 'conversation')}/unread`, {}),

  deleteConversation: (id, permanent = false) =>
    secureApiService.delete(`/admin/messages/${validateId(id, 'conversation')}${permanent ? '?permanent=1' : ''}`),

  getMessagesStats: () =>
    secureApiService.get('/admin/messages/stats', {}, { useCache: false }),

  searchConversations: (q, limit = 20) =>
    secureApiService.get('/admin/messages/search', { params: { q, limit } }, { useCache: false }),

  getMessageAgents: () =>
    secureApiService.get('/admin/messages/agents', {}, { useCache: false }),

  getReplyTemplates: () =>
    secureApiService.get('/admin/messages/templates', {}, { useCache: false }),

  bulkMessageAction: (ids, action, value = null) =>
    secureApiService.post('/admin/messages/bulk', { ids, action, value }),

  // 🏢 AGENCES (hiérarchie SaaS)
  getAgences: () =>
    secureApiService.get('/admin/agences', {}, { useCache: false }),
  getAgence: (id) =>
    secureApiService.get(`/admin/agences/${validateId(id, 'agence')}`, {}, { useCache: false }),
  createAgence: (data) =>
    secureApiService.post('/admin/agences', data),
  updateAgence: (id, data) =>
    secureApiService.put(`/admin/agences/${validateId(id, 'agence')}`, data),
  deleteAgence: (id) =>
    secureApiService.delete(`/admin/agences/${validateId(id, 'agence')}`),

  // 💳 ABONNEMENTS (plans SaaS)
  getPlans: () =>
    secureApiService.get('/admin/subscriptions/plans', {}, { useCache: true, cacheTimeout: 300000 }),
  getMySubscription: () =>
    secureApiService.get('/admin/subscriptions/me', {}, { useCache: false }),
  getCompanySubscription: (companyId) =>
    secureApiService.get(`/admin/subscriptions/company/${validateId(companyId, 'compagnie')}`, {}, { useCache: false }),
  // overrides = { override_agences, override_agents, override_chauffeurs, override_trajets_actifs } (vide = hériter du plan)
  assignPlan: (companyId, plan_code, overrides = {}, expires_at = null) =>
    secureApiService.post(`/admin/subscriptions/company/${validateId(companyId, 'compagnie')}`, { plan_code, expires_at, ...overrides }),
  updatePlan: (code, data) =>
    secureApiService.put(`/admin/subscriptions/plans/${encodeURIComponent(code)}`, data),

  // 👥 ÉQUIPE — attribution des rôles agents / chef d'agence (scopé compagnie)
  getTeamMembers: (companyId = null) =>
    secureApiService.get('/admin/team/members', companyId ? { params: { company_id: companyId } } : {}, { useCache: false }),
  getUserRoles: (userId) =>
    secureApiService.get(`/admin/team/users/${validateId(userId, 'utilisateur')}/roles`, {}, { useCache: false }),
  assignTeamRole: (data) =>
    secureApiService.post('/admin/team/assign', data),
  revokeTeamRole: (data) =>
    secureApiService.post('/admin/team/revoke', data),
  createMember: (data) =>
    secureApiService.post('/admin/team/create-member', data),
  // U-2 — suppression d'un membre (soft/hard auto côté serveur, isolé company_id, gardes)
  deleteMember: (userId) =>
    secureApiService.delete(`/admin/team/users/${validateId(userId, 'utilisateur')}`),

  // 🔐 PERMISSIONS GRANULAIRES (catalogue + presets + attribution par utilisateur)
  getPermissionCatalog: (companyId = null) =>
    secureApiService.get('/admin/team/permissions/catalog', companyId ? { params: { company_id: companyId } } : {}, { useCache: false }),
  getPermissionPresets: () =>
    secureApiService.get('/admin/team/permissions/presets', {}, { useCache: false }),
  // 🧩 Rôles assignables RÉELS de la compagnie (Phase 1 unification) : company_roles personnalisés
  // + presets système. Remplace la liste figée ASSIGNABLE_STAFF_ROLES côté écran Équipe.
  getCompanyRoles: (companyId = null) =>
    secureApiService.get('/admin/team/company-roles', companyId ? { params: { company_id: companyId } } : {}, { useCache: false }),
  getUserPermissions: (userId, companyId = null) =>
    secureApiService.get(`/admin/team/users/${validateId(userId, 'utilisateur')}/permissions`, companyId ? { params: { company_id: companyId } } : {}, { useCache: false }),
  setUserPermissions: (userId, data) =>
    secureApiService.post(`/admin/team/users/${validateId(userId, 'utilisateur')}/permissions`, data),

  // 🏢 LISTE DES COMPAGNIES (sélecteur SUPER_ADMIN)
  getCompanies: (params = {}) =>
    secureApiService.get('/admin/super/companies', { params }, { useCache: true, cacheTimeout: 60000 }),

  // 🧭 CONTEXTE EFFECTIF (rôle, permissions, plan, features) — source de vérité des menus
  getMyContext: () =>
    secureApiService.get('/admin/me/context', {}, { useCache: false }),
};